import { FeConfigType } from "config/config";
import { getIsDisabled, getIsReadonly, getRecordConfig } from "config/resourceUtils";
import workspaceConfig from "config/resources/workspace";
import { getUserData } from "containers/Auth/Auth.selectors";
import { getBundles } from "containers/Bundles/Bundles.selectors";
import {
    resourceDataFetchingSelector,
    resourceDataSelector,
} from "containers/Resources/Resources.selectors";
import { Bundle, Record } from "graphql/generated/graphql";
import qs from "qs";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { useRouteMatch } from "react-router";
import { getIri } from "utils/utils";

export const getRecordUID = () => {
    const queryParams = qs.parse(window?.location?.search.substring(1));
    const uid = queryParams?.id ?? queryParams?.name;
    const hasNameQueryParam = queryParams?.name != null;
    return { recordUID: uid, hasNameQueryParam };
};

export interface MatchParams {
    resourceName: FeConfigType;
    recordId: string;
}

export const useResourceData = () => {
    const resourceMatch = useRouteMatch<MatchParams>({
        path: "/:resourceName",
        exact: true,
    });

    const newResourceMatch = useRouteMatch<MatchParams>({
        path: "/:resourceName/new",
        exact: true,
    });

    // Combine matches
    const match = resourceMatch || newResourceMatch;
    // const match = useRouteMatch<MatchParams>({ path: "/:resourceName", exact: true });
    const userData = useSelector(getUserData);
    const resourceName = match?.params?.resourceName as FeConfigType;
    const queryParams = qs.parse(window?.location?.search.substring(1));
    const workspaceID = userData?.workspace_id || userData?.workspace_name;
    const isWorkspace = workspaceConfig.resourceName === resourceName;
    const { recordUID, hasNameQueryParam } = getRecordUID();
    const uid = recordUID ?? (isWorkspace ? workspaceID : undefined);
    const iri = getIri(resourceName, uid);
    const resourceData: Record = useSelector(resourceDataSelector(iri));
    const resourceDataFetching = useSelector(resourceDataFetchingSelector(uid));
    const isFetching = resourceDataFetching.includes(iri);
    const resourceConfig = getRecordConfig(resourceName);
    const isReadOnly = getIsReadonly(resourceConfig, resourceData);
    const isDisabled = getIsDisabled(resourceConfig, resourceData);

    const recordId: string | undefined = (queryParams?.id as string) ?? resourceData?.id;
    const bundleList: Bundle[] = useSelector(getBundles);

    // retrieve bundle data from /ui bundle list websocket and merge it with resourceData
    const mergedData = useMemo(() => {
        if (resourceName === "bundle") {
            const bundleData = bundleList.find((bundle) => bundle.id === recordId);
            const { is_dirty, is_readonly, version } = bundleData || {};
            const mergedData = resourceData
                ? { ...resourceData, is_dirty, is_readonly: is_readonly ?? false, version }
                : undefined;
            return mergedData;
        } else {
            return resourceData;
        }
    }, [bundleList, recordId, resourceData, resourceName]);

    return {
        resourceData: mergedData,
        isFetching,
        userData,
        isReadOnly,
        isDisabled,
        iri,
        resourceName,
        uid,
        recordId,
        hasNameQueryParam,
        resourceConfig,
        isWorkspace,
    };
};
